import React from "react";
import {
  Stack,
  Checkbox,
  Typography,
  TableRow,
  TableCell,
} from "@mui/material";
import { useFormContext } from "react-hook-form";

import { HubIcon } from "../../components";
import {
  formatDateMmDdYyyy,
  formatDateTimeMmDdYyyy,
} from "../../helpers/dateHelper";
import { useStationListWithCache } from "../../components/StationHookFormSelect";

import WorkOrderRoutingSubRow from "./WorkOrderRoutingSubRow";
import WorkOrderContextMenu from "./WorkOrderContextMenu";

import { condition } from "./helper";
import { useGetLaborDataByRouteId } from "../../hooks";
import { useEmployeeScanContext } from "../../hooks/contexts/EmployeeScanContextProvider";

export const RouteCell = ({
  value,
  defaultValue = "MM/DD/YYYY",
  icon = "",
  sx = {},
}) => {
  return (
    <TableCell sx={sx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        width="100%"
        alignItems="center"
      >
        <Typography
          sx={{ fontSize: 11 }}
          variant={value ? "caption" : "captionGrey"}
        >
          {value ?? defaultValue}
        </Typography>

        <HubIcon icon={icon} size="12px" sx={{ color: "hubColors.grey" }} />
      </Stack>
    </TableCell>
  );
};

const WorkOrderRoutingEditRow = ({
  item,
  idx,
  open,
  handleRowClick = () => {},
  setRouteId = () => {},
  setEditId = () => {},
  showQuantity = false,
  ...rest
}) => {
  const { getValues } = useFormContext();

  const { stations } = useStationListWithCache();
  const station = stations?.find((s) => s.id === item?.stationId);

  const currentRoute = getValues("route");
  const isCurrent = item?.id === currentRoute;
  const isDone = !!item?.endDate;
  const hasStarted = !!item?.startDate;
  const plannedStartDate = item?.plannedStartDate;
  const plannedEndDate = item?.plannedEndDate;
  const isOpen = open instanceof Set ? open.has(item.id) : open === item.id;

  const { scannedEmployee, canEditLabor } = useEmployeeScanContext();
  const laborUserIds = item?.laborUserIds ?? [];
  const isGreyedOut =
    laborUserIds.length === 0 ||
    (!!scannedEmployee &&
      !laborUserIds.some((userId) => canEditLabor?.(userId)));

  const conditionProps = condition({ ...item, isCurrent });

  return (
    <>
      <TableRow
        backgroundColor="hubColors.white"
        {...rest}
        {...conditionProps}
        sx={{
          ...conditionProps.sx,
          ...(isGreyedOut && { backgroundColor: "hubColors.greyLightest" }),
        }}
      >
        <TableCell
          sx={{ cursor: "pointer" }}
          onClick={() => handleRowClick(item.id)}
          variant="small"
        >
          <HubIcon icon={isOpen ? "ChevronDown" : "ChevronRight"} size="12px" />
        </TableCell>
        <TableCell sx={{ minWidth: "250px" }}>
          <Stack direction="row" gap="10px" alignItems="center">
            <Checkbox sx={{ width: "14px", padding: 0 }} checked={isDone} />
            <Typography variant="caption">{station?.label}</Typography>
          </Stack>
        </TableCell>
        {showQuantity && (
          <RouteCell value={item?.quantity || 0} sx={{ width: "150px" }} />
        )}
        <RouteCell value={item?.completed || 0} sx={{ width: "150px" }} />
        <RouteCell value={item?.scrapped || 0} sx={{ width: "150px" }} />
        <RouteCell
          value={formatDateMmDdYyyy(plannedStartDate)}
          sx={{ width: "150px" }}
        />
        <RouteCell
          value={formatDateMmDdYyyy(plannedEndDate)}
          sx={{ width: "150px" }}
        />
        <RouteCell
          value={formatDateTimeMmDdYyyy(item?.startDate)}
          defaultValue="MM/DD/YYYY"
          sx={{ width: "150px" }}
        />
        <RouteCell
          value={formatDateTimeMmDdYyyy(item?.endDate)}
          defaultValue="MM/DD/YYYY"
          sx={{ width: "150px" }}
        />
        <TableCell variant="small">
          <WorkOrderContextMenu
            id={idx}
            routeId={item?.id}
            started={hasStarted}
            ended={isDone}
            setRouteId={setRouteId}
            setEditId={setEditId}
          />
        </TableCell>
      </TableRow>
      {isOpen && item?.id && (
        <WorkOrderRoutingSubRow
          routeId={item?.id}
          isOpen={isOpen}
          showQuantity={showQuantity}
        />
      )}
    </>
  );
};

export default WorkOrderRoutingEditRow;
